import React from 'react';
import {observer} from 'mobx-react';
import {AutoSizer, Column, Table} from 'react-virtualized';
import {stylesheet} from 'typestyle';
import {important} from 'csx';
import {evenRowClassName, oddRowClassName} from '../../styles/style';
import {KPIGroupKey, SessionInsightKPI} from '../../kpi';
import {formatValue, moneyFormat} from '../../utility/FormatValueUtils';
import InsightsTableStore, {InsightsTableMode} from '../../stores/InsightsTableStore';
import {TableSortDirection} from '../../types/Table';
import {MessageProvider, TProvider} from '../../i18n/i18n';
import {Channel} from '../../types/Channel';

interface InsightsTableProps {
    insightsTableStore: InsightsTableStore;
    getSelectedChannel: () => Channel | undefined;
    windowHeight: number;
}

@observer
export default class InsightsTable extends React.Component<InsightsTableProps> {
    private rowGetter = ({index}) => {
        return this.props.insightsTableStore.insightsTableData.data[index];
    };

    private tableColumns(t: MessageProvider) {
        const mode = this.getMode();

        let label;
        if (mode === InsightsTableMode.ProductsForSearchTerm) {
            label = t('insights:productName');
        } else if (mode === InsightsTableMode.SearchTermsForProduct) {
            label = t('insights:searchTerm');
        }

        return [
            <Column
                key={1}
                label={label}
                dataKey={KPIGroupKey.Searchterm}
                cellRenderer={({cellData}) => cellData}
                cellDataGetter={(cellData) => {
                    const cellValue = cellData.rowData[cellData.dataKey];
                    return <div>{cellValue}</div>;
                }}
                flexGrow={3}
                width={400}
            />,
            <Column
                key={2}
                label={
                    <div title={t('insights:clickCounts')} style={{position: 'absolute', right: 0, bottom: 5}}>
                        <i className="fa fa-mouse-pointer" aria-hidden="true" />
                    </div>
                }
                dataKey={SessionInsightKPI.ClickCount}
                cellRenderer={({cellData}) => cellData}
                cellDataGetter={(cellData) => <div style={{textAlign: 'right'}}>{cellData.rowData[cellData.dataKey]}</div>}
                flexGrow={1}
                width={50}
            />,
            <Column
                key={3}
                label={
                    <div title={t('insights:cartCounts')} style={{position: 'absolute', right: 0, bottom: 5}}>
                        <i className="fa fa-shopping-cart" aria-hidden="true" />
                    </div>
                }
                dataKey={SessionInsightKPI.CartTotalAmount}
                cellRenderer={({cellData}) => cellData}
                cellDataGetter={(cellData) => <div style={{textAlign: 'right'}}>{cellData.rowData[cellData.dataKey]}</div>}
                flexGrow={1}
                width={50}
            />,
            <Column
                key={4}
                label={
                    <div title={t('insights:checkoutCounts')} style={{position: 'absolute', right: 0, bottom: 5}}>
                        <i className="fa fa-credit-card" aria-hidden="true" />
                    </div>
                }
                dataKey={SessionInsightKPI.CheckoutTotalAmount}
                cellRenderer={({cellData}) => cellData}
                cellDataGetter={(cellData) => <div style={{textAlign: 'right'}}>{cellData.rowData[cellData.dataKey]}</div>}
                flexGrow={1}
                width={50}
            />,
            <Column
                key={5}
                label={
                    <div title={t('insights:Sales')} style={{position: 'absolute', right: 0, bottom: 5}}>
                        <i className="fa fa-eur" aria-hidden="true" />
                    </div>
                }
                dataKey={SessionInsightKPI.Sales}
                cellRenderer={({cellData}) => cellData}
                cellDataGetter={(cellData) => (
                    <div style={{textAlign: 'right'}}>
                        {formatValue(cellData.rowData[cellData.dataKey], moneyFormat(this.props.getSelectedChannel()))}
                    </div>
                )}
                flexGrow={1}
                width={200}
            />,
        ];
    }

    private getTableColumns(t: MessageProvider) {
        switch (this.getMode()) {
            case InsightsTableMode.ProductsForSearchTerm:
            case InsightsTableMode.SearchTermsForProduct:
                return this.tableColumns(t);
            default:
                throw Error('Unknown additional information table mode ' + this.getMode());
        }
    }

    private getNoDataMessage(t: MessageProvider) {
        switch (this.getMode()) {
            case InsightsTableMode.ProductsForSearchTerm:
                return <div>{t('insights:noProductsForSearchTerm')}</div>;
            case InsightsTableMode.SearchTermsForProduct:
                return <div>{t('insights:noSearchTermsForProduct')}</div>;
            default:
                throw Error('Unknown additional information table mode ' + this.getMode());
        }
    }

    private getMode() {
        const {insightsModalSetting} = this.props.insightsTableStore;

        return insightsModalSetting?.mode;
    }

    public getRowClassName = ({index}) => {
        if (index < 0) {
            return evenRowClassName;
        }

        return index % 2 === 0 ? evenRowClassName : oddRowClassName;
    };

    public render() {
        const {getSessionApiKeyIfAvailable, tableSorting, setTableSort, insightsTableData} = this.props.insightsTableStore;

        const tableHeight = this.props.windowHeight - 160;

        return insightsTableData.data.length > 0 ? (
            <TProvider>
                {(t: MessageProvider) => (
                    <AutoSizer style={{height: 'auto', width: 'auto'}}>
                        {({width}) => (
                            <Table
                                className={tableCss.table}
                                height={tableHeight}
                                width={width}
                                style={{minWidth: '750px'}}
                                rowHeight={30}
                                rowClassName={this.getRowClassName}
                                headerHeight={40}
                                sortBy={getSessionApiKeyIfAvailable(tableSorting.attribute)}
                                sortDirection={tableSorting.sortDirection}
                                sort={(sortPackage) => {
                                    if (sortPackage.sortBy === 'sid') {
                                        // sorting sids is not a feature - yet
                                        return null;
                                    }
                                    setTableSort(sortPackage.sortBy as SessionInsightKPI, sortPackage.sortDirection as TableSortDirection);
                                    return sortPackage;
                                }}
                                rowCount={insightsTableData.data.length}
                                rowGetter={this.rowGetter}>
                                {this.getTableColumns(t)}
                            </Table>
                        )}
                    </AutoSizer>
                )}
            </TProvider>
        ) : (
            <TProvider>{(t: MessageProvider) => <>{this.getNoDataMessage(t)}</>}</TProvider>
        );
    }
}

const tableCss = stylesheet({
    table: {
        $nest: {
            '& .ReactVirtualized__Table__Grid': {
                overflowY: important('scroll'),
            },
        },
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/InsightsTable.tsx